import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import LoadingZone from 'components/LoadingZone';
import usePageTitle from 'utils/hooks/usePageTitle';
import * as CheckoutActions from 'components/Checkout/redux/actions';
import {
  selectAllEventsInCart,
  selectIsEditingEventRegistration,
} from '../redux/selectors';
import EventsYouRegisteredFor from './EventsYouRegisteredFor';
import AdditionalInfoConfirmation from './AdditionalInfoConfirmation';
import useFinalizePaidEventRegistration from './useFinalizePaidEventRegistration';

const EventConfirmation = () => {
  const [bIsLoading, fnSetIsLoading] = useState(true);
  const sEmail = useSelector((oState) => oState.Checkout.email);
  const iTotalCost = useSelector((oState) => oState.Checkout.checkoutTotal);
  const aEventsInCart = useSelector(selectAllEventsInCart);
  const bIsEditingRegistration = useSelector(selectIsEditingEventRegistration);
  const [oSearchParams] = useSearchParams();
  const sPaymentTransactionId = oSearchParams.get('transid');
  const fnDispatch = useDispatch();

  let sConfirmationMsg = '';
  if (aEventsInCart.length > 0) {
    sConfirmationMsg =
      aEventsInCart[0]?.oDetails?.oEvent?.REGISTRATION.CONFIRMATION_MESSAGE;
  }

  const oEventContact = useMemo(() => {
    if (aEventsInCart.length > 0) {
      // Use the main event contact if the user registered for a sub-event
      return aEventsInCart[0].oDetails.oEvent.MAIN_EVENT
        ? aEventsInCart[0].oDetails.oEvent.MAIN_EVENT.CONTACT
        : aEventsInCart[0].oDetails.oEvent.CONTACT;
    }
    return {};
  }, [aEventsInCart]);

  useFinalizePaidEventRegistration({
    fnSetIsLoading,
  });

  usePageTitle('Event Registration - Confirmation');

  // Reset the cart when the component unmounts
  useEffect(
    () => () => {
      fnDispatch(CheckoutActions.reset());
    },
    [fnDispatch]
  );

  return (
    <LoadingZone
      isLoading={bIsLoading}
      message="We're saving your registration. Please do not use the back button."
    >
      <div className='eventConfirmation'>
        {bIsEditingRegistration ? (
          <h1 className='eventConfirmation__message t-paragraph--large'>
            Your registration has been updated.
          </h1>
        ) : (
          <div
            className='eventConfirmation__message t-paragraph--large'
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sConfirmationMsg,
            }}
          />
        )}
        <p className='eventConfirmation__email'>
          {sEmail ? (
            <span>
              We sent your registration confirmation to{' '}
              <strong data-cy='email-address'>{sEmail}</strong>.
            </span>
          ) : (
            'Please print or save this confirmation page for your records.'
          )}
        </p>
        {iTotalCost > 0 && (
          <p
            className='eventConfirmation__transactionNumber t-paragraph--small'
            data-cy='confirmation-transaction-confirmation'
          >
            Transaction Confirmation: #
            <span data-cy='confirmation-transaction-id'>
              {sPaymentTransactionId}
            </span>
          </p>
        )}
        <EventsYouRegisteredFor />
        <AdditionalInfoConfirmation />
        <p className='eventConfirmation__contact'>
          If you have questions about your registration, please contact{' '}
          <span className='t-paragraph--medium'>{oEventContact.NAME}</span> at{' '}
          <a
            href={`mailto:${oEventContact.EMAIL}`}
            className='t-paragraph--medium'
          >
            {oEventContact.EMAIL}
          </a>
          .
        </p>
        <Link to='/main' className='eventConfirmation__backToHome button'>
          Return to home page
        </Link>
      </div>
    </LoadingZone>
  );
};

export default EventConfirmation;
